'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  id: number;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

const TrustMetrics = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState<number[]>([0, 0, 0, 0]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: Metric[] = [
    {
      id: 1,
      value: 25,
      suffix: "+",
      label: "Yıllık Tecrübe",
      icon: "CalendarDaysIcon"
    },
    {
      id: 2,
      value: 500,
      suffix: "+",
      label: "Tamamlanan Proje",
      icon: "BuildingOffice2Icon"
    },
    {
      id: 3,
      value: 98,
      suffix: "%",
      label: "Müşteri Memnuniyeti",
      icon: "FaceSmileIcon"
    },
    {
      id: 4,
      value: 150,
      suffix: "+",
      label: "Uzman Ekip",
      icon: "UsersIcon"
    }
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isHydrated]);

  useEffect(() => {
    if (!isVisible || !isHydrated) return;

    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    metrics.forEach((metric, index) => {
      let currentStep = 0;
      const increment = metric.value / steps;

      const timer = setInterval(() => {
        currentStep++;
        setCounts((prev) => {
          const newCounts = [...prev];
          newCounts[index] = Math.min(Math.round(increment * currentStep), metric.value);
          return newCounts;
        });

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  }, [isVisible, isHydrated]);

  return (
    <section data-component-id="src/app/homepage/components/TrustMetrics.tsx:105:4" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="105" data-component-end-line="141" data-component-file="TrustMetrics.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20lg%3Apy-24%20bg-card%22%7D" ref={sectionRef} className="py-16 lg:py-24 bg-card">
      <div data-component-id="src/app/homepage/components/TrustMetrics.tsx:106:6" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="106" data-component-end-line="140" data-component-file="TrustMetrics.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-7xl%20mx-auto%20px-6%20lg%3Apx-12%22%7D" className="w-full max-w-7xl mx-auto px-6 lg:px-12">
        <div data-component-id="src/app/homepage/components/TrustMetrics.tsx:107:8" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="107" data-component-end-line="139" data-component-file="TrustMetrics.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-8%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {metrics.map((metric, index) => (
            <div data-component-id="src/app/homepage/components/TrustMetrics.tsx:109:12" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="109" data-component-end-line="137" data-component-file="TrustMetrics.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20text-center%20p-8%20bg-background%20rounded-lg%20shadow-md%20hover%3Ashadow-lg%20transition-shadow%20duration-300%22%7D"
              key={metric.id}
              className="flex flex-col items-center text-center p-8 bg-background rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              {/* Icon */}
              <div data-component-id="src/app/homepage/components/TrustMetrics.tsx:114:14" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="114" data-component-end-line="121" data-component-file="TrustMetrics.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20flex%20items-center%20justify-center%20bg-primary%2F10%20rounded-full%20mb-4%22%7D" className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mb-4">
                <Icon data-component-id="src/app/homepage/components/TrustMetrics.tsx:115:16" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="115" data-component-end-line="120" data-component-file="TrustMetrics.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-primary%22%7D"
                  name={metric.icon as any}
                  size={32}
                  variant="solid"
                  className="text-primary"
                />
              </div>

              {/* Counter */}
              <div data-component-id="src/app/homepage/components/TrustMetrics.tsx:124:14" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="124" data-component-end-line="131" data-component-file="TrustMetrics.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-2%22%7D" className="mb-2">
                <span data-component-id="src/app/homepage/components/TrustMetrics.tsx:125:16" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="125" data-component-end-line="127" data-component-file="TrustMetrics.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-5xl%20font-bold%20font-headline%20text-primary%22%7D" className="text-5xl font-bold font-headline text-primary">
                  {isHydrated ? counts[index] : metric.value}
                </span>
                <span data-component-id="src/app/homepage/components/TrustMetrics.tsx:128:16" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="128" data-component-end-line="130" data-component-file="TrustMetrics.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-4xl%20font-bold%20font-headline%20text-primary%22%7D" className="text-4xl font-bold font-headline text-primary">
                  {metric.suffix}
                </span>
              </div>

              {/* Label */}
              <p data-component-id="src/app/homepage/components/TrustMetrics.tsx:134:14" data-component-path="src/app/homepage/components/TrustMetrics.tsx" data-component-line="134" data-component-end-line="136" data-component-file="TrustMetrics.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20font-semibold%20font-body%20text-foreground%22%7D" className="text-base font-semibold font-body text-foreground">
                {metric.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustMetrics;